import React from "react";
import { Box, Paper, Typography, Button, Alert } from "@mui/material";
import globals from "../../Globals";
import { PageContainer, SectionHeader, SelectorBar, SelectField, DataTable, StatCard } from "../../Layout/Common";
import { FIXED_TABLE } from "../Localization/loc";
import { market, savedBranch, saveBranch, canEdit, num, ConfirmAction } from "./community";

// Market → Stats (service `market`, Docs/Market.md §9 / §10): `market stats <branch>` - open offers / units / best price / the currency
// buys hold per goods and side, trades / units / volume / fees of the last 24 h and 7 days - and `market sweep` (one run of the
// scheduler's market.sweep:<title> now, every branch: the interrupted steps finished, the expired offers given back, the clean pass).
// The backend keeps no "last sweep" for the admin: the panel shows the runs started from this page. `#market` views, `market` sweeps.
const sum = (rows, key) => (rows ?? []).reduce((n, r) => n + (Number(r?.[key]) || 0), 0);

export default class MarketStatsTab extends React.Component {
    state = { branch: savedBranch(), stats: null, error: null, loading: false, busy: false, action: null, sweeps: [] };

    componentDidMount() { this.load(); }

    load = () => {
        saveBranch(this.state.branch);
        this.setState({ loading: true, error: null });
        market("Loading the market stats", ["stats", this.state.branch], { silent: true })
            .then(r => this.setState({ stats: r && typeof r === "object" ? r : null }))
            .catch(e => this.setState({ stats: null, error: e?.message ?? "failed" }))
            .finally(() => this.setState({ loading: false }));
    }

    sweep = () => this.setState({ action: {
        title: "Run the market sweep now?", confirmText: "Run sweep",
        text: "One run of market.sweep (every branch of the title): steps older than 10 minutes finished, expired offers given back, accounts not checked for 6 h re-checked (unclean ones cancelled). The scheduler runs it every 10 minutes anyway.",
        run: () => {
            this.setState({ busy: true });
            return market("Running the sweep", ["sweep"], { successMessage: "Sweep done" })
                .then(r => { this.setState(s => ({ action: null, sweeps: [{ at: new Date(), done: r?.done ?? JSON.stringify(r) }, ...s.sweeps].slice(0, 5) })); this.load(); })
                .catch(() => {})
                .finally(() => this.setState({ busy: false }));
        },
    }});

    render() {
        const { branch, stats, error, loading, busy, action, sweeps } = this.state;
        const edit = canEdit("market");
        const open = stats?.open ?? [], day = stats?.last24h ?? [], week = stats?.last7d ?? [];
        const sells = open.filter(o => o.side === "sell"), buys = open.filter(o => o.side === "buy");
        const openColumns = [
            { id: "goods", label: "Goods", width: "20%", render: o => <span style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{o.goods}</span> },
            { id: "side", label: "Side", width: "12%" },
            { id: "offers", label: "Offers", width: "16%", align: "right", render: o => num(o.offers) },
            { id: "units", label: "Units", width: "16%", align: "right", render: o => num(o.units) },
            { id: "best", label: "Best price", width: "18%", align: "right", render: o => num(o.best) },
            { id: "held", label: "Currency held", width: "18%", align: "right", render: o => o.side === "buy" ? num(o.held) : "—" },
        ];
        const goodsIds = [...new Set([...day.map(t => t.goods), ...week.map(t => t.goods)])].sort();
        const tradeRows = goodsIds.map(goods => ({ goods, d: day.find(t => t.goods === goods), w: week.find(t => t.goods === goods) }));
        const tradeColumns = [
            { id: "goods", label: "Goods", width: "20%", render: r => <span style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{r.goods}</span> },
            { id: "dTrades", label: "24 h trades", width: "10%", align: "right", render: r => num(r.d?.trades) },
            { id: "dUnits", label: "24 h units", width: "10%", align: "right", render: r => num(r.d?.units) },
            { id: "dVolume", label: "24 h volume", width: "10%", align: "right", render: r => num(r.d?.volume) },
            { id: "dFees", label: "24 h fees", width: "10%", align: "right", render: r => num(r.d?.fees) },
            { id: "wTrades", label: "7 d trades", width: "10%", align: "right", render: r => num(r.w?.trades) },
            { id: "wUnits", label: "7 d units", width: "10%", align: "right", render: r => num(r.w?.units) },
            { id: "wVolume", label: "7 d volume", width: "10%", align: "right", render: r => num(r.w?.volume) },
            { id: "wFees", label: "7 d fees", width: "10%", align: "right", render: r => num(r.w?.fees) },
        ];
        return (
            <PageContainer>
                <SectionHeader title="Market stats">
                    <Typography variant="body2" color="text.secondary">{globals.selectedTitle} · {stats?.titleBranch ?? branch} · fee {stats?.feePct ?? "?"} % on the seller's side</Typography>
                </SectionHeader>
                <SelectorBar>
                    <SelectField label="Branch" size="small" minWidth={160} value={branch} options={globals.branches} onChange={v => this.setState({ branch: v }, this.load)} />
                    <Button variant="outlined" onClick={this.load} disabled={loading}>{loading ? "Loading…" : "Reload"}</Button>
                    {edit && <Button variant="contained" color="warning" onClick={this.sweep} disabled={busy}>Run sweep</Button>}
                </SelectorBar>
                {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
                {sweeps.length > 0 && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => this.setState({ sweeps: [] })}>
                        {sweeps.map((s, i) => <div key={i}>{s.at.toLocaleString()}: {s.done}</div>)}
                    </Alert>
                )}
                {stats && (
                    <>
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                            <StatCard label="Open offers" value={num(sum(open, "offers"))} secondary={`${num(sum(sells, "offers"))} sell · ${num(sum(buys, "offers"))} buy`} />
                            <StatCard label="Units on sale" value={num(sum(sells, "units"))} secondary={`${num(sum(buys, "units"))} units wanted`} />
                            <StatCard label="Currency held" value={num(sum(buys, "held"))} secondary="by open buys" />
                            <StatCard label="Volume 24 h" value={num(sum(day, "volume"))} secondary={`${num(sum(day, "trades"))} trades · fees ${num(sum(day, "fees"))}`} />
                            <StatCard label="Volume 7 d" value={num(sum(week, "volume"))} secondary={`${num(sum(week, "trades"))} trades · fees ${num(sum(week, "fees"))}`} />
                        </Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Open offers per goods</Typography>
                        {open.length > 0
                            ? <DataTable sx={FIXED_TABLE} columns={openColumns} rows={open} getRowKey={o => `${o.goods}-${o.side}`} />
                            : <Paper sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">No open offer on this branch.</Typography></Paper>}
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Trades and fees</Typography>
                        {tradeRows.length > 0
                            ? <DataTable sx={FIXED_TABLE} columns={tradeColumns} rows={tradeRows} getRowKey={r => r.goods} />
                            : <Paper sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">No trade in 7 days on this branch.</Typography></Paper>}
                    </>
                )}
                {!stats && !error && <Paper sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">Loading…</Typography></Paper>}
                <ConfirmAction action={action} busy={busy} onClose={() => this.setState({ action: null })} />
            </PageContainer>
        );
    }
}
